import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { StoryCard, Playlist, UserProfile, IdentityMoment, IdentityMomentType, IdentityInsights, MeaningLayers, CivicMood, MoodEntry, MoodFrequency, SongSubmissionFormData, UserSongEngagement, IssueTag } from './types';
import { MOCK_CARDS, MOCK_PLAYLISTS, getCardsWithStorageUrls, getPlaylistsWithStorageUrls } from './mockData';
import { LEGACY_INTEREST_MAPPING } from './constants';
import { getTodaysHeroSong, recordHeroViewed } from './heroService';
import { sortHomeCards, sortFilteredCards, sortSavedCardsByEngagement, sortPlaylists } from './sortingUtils';
import { supabase } from './supabaseClient';
import {
  logUserEvent,
  getActiveSongsWithIssues,
  getAllPlaylists,
  getUserSavedSongs,
  saveSongForUser,
  unsaveSongForUser,
  getProfile,
  upsertProfile,
  getDerivedIdentityMoments,
  getDerivedMoodEntries,
  getDerivedCardViewHistory,
  getUserSessionState,
  upsertUserSessionState,
  deleteAllUserData,
} from './dataClient';
import { transformSongsToCards, transformPlaylistsToUI, transformSongDataToCards } from './dataTransformers';
import { getSongs } from './songDataClient';
import { upsertUserPreferences } from './civicbeatData';
import { trackEvent } from './analytics';

function migrateInterests(interests: string[]): string[] {
  return interests
    .map(interest => LEGACY_INTEREST_MAPPING[interest] || interest)
    .filter((interest, index, self) => self.indexOf(interest) === index);
}

interface AppState {
  userProfile: UserProfile | null;
  savedCardIds: string[];
  savedPlaylistIds: string[];
  viewedCardIds: string[];
  identityMoments: IdentityMoment[];
  cardViewHistory: Record<string, number>;
  moodEntries: MoodEntry[];
  songEngagements: UserSongEngagement[];
  currentScreen: string;
  currentCardId: string | null;
  restorationState: any;
  selectedTopicFilter: string;
  showActionWheel: boolean;
  toastMessage: { title: string; subtitle?: string } | null;
  showReflectionPrompt: boolean;
  reflectionPromptIssue: string | null;
  songSubmissionFormData: SongSubmissionFormData;
  isLoadingData: boolean;

  updateUserInterests: (interests: string[]) => void;
  // resetProfile: () => void; // DEPRECATED: Removed destructive reset feature
  navigateTo: (screen: string, cardId?: string, state?: any) => void;
  navigateReplacingCurrent: (screen: string, cardId?: string, state?: any) => void;
  navigateBack: () => void;
  clearRestorationState: () => void;
  setCurrentCardId: (cardId: string | null) => void;
  toggleSaveCard: (cardId: string) => void;
  toggleSavePlaylist: (playlistId: string) => void;
  markCardViewed: (cardId: string) => void;
  setTopicFilter: (topic: string) => void;
  setShowActionWheel: (show: boolean) => void;
  addIdentityMoment: (cardId: string, type: IdentityMomentType, issueTags: string[]) => void;
  removeIdentityMoment: (cardId: string, type: IdentityMomentType) => void;
  toggleIdentityMoment: (cardId: string, type: IdentityMomentType, issueTags: string[]) => void;
  getIdentityInsights: () => IdentityInsights;
  getMeaningLayers: () => MeaningLayers;
  showToast: (title: string, subtitle?: string) => void;
  clearToast: () => void;
  dismissReflectionPrompt: () => void;
  getCards: () => StoryCard[];
  getPlaylists: () => Playlist[];
  getFeaturedCard: () => StoryCard | null;
  trackMood: (cardId: string, mood: CivicMood, songTitle: string, artistName: string) => void;
  getMoodsForCard: (cardId: string) => CivicMood[];
  getMoodFrequency: () => MoodFrequency;
  getRecentMoodEntries: (limit?: number) => MoodEntry[];
  findRelatedStory: (cardId: string) => StoryCard | null;
  getMeaningMix: () => StoryCard[];
  updateSongSubmissionFormData: (data: Partial<SongSubmissionFormData>) => void;
  clearSongSubmissionFormData: () => void;
  refreshSongs: () => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AppContext = createContext<AppState | undefined>(undefined);

// ===== EVENT LOGGING: Import useAuth to get current user =====
import { useAuth } from './AuthContext';
// ===== END EVENT LOGGING =====

export function AppProvider({ children }: { children: ReactNode }) {
  // ===== SUPABASE MIGRATION: Get current user and load data from DB =====
  const { user, loading: authLoading } = useAuth();
  const [allSongs, setAllSongs] = useState<StoryCard[]>([]);
  const [allPlaylists, setAllPlaylists] = useState<Playlist[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  // ===== END SUPABASE MIGRATION =====

  // ===== SUPABASE MIGRATION: Remove localStorage, initialize with defaults =====
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [savedCardIds, setSavedCardIds] = useState<string[]>([]);
  const [savedPlaylistIds, setSavedPlaylistIds] = useState<string[]>([]);
  const [viewedCardIds, setViewedCardIds] = useState<string[]>([]);
  const [identityMoments, setIdentityMoments] = useState<IdentityMoment[]>([]);
  const [cardViewHistory, setCardViewHistory] = useState<Record<string, number>>({});
  const [moodEntries, setMoodEntries] = useState<MoodEntry[]>([]);
  // ===== END SUPABASE MIGRATION =====

  const [currentScreen, setCurrentScreen] = useState<string>('home');
  const [currentCardId, setCurrentCardId] = useState<string | null>(null);
  const [restorationState, setRestorationState] = useState<any>(null);
  const [navigationHistory, setNavigationHistory] = useState<Array<{ screen: string; cardId: string | null; state?: any }>>([]);
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<string>('All');
  const [showActionWheel, setShowActionWheel] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<{ title: string; subtitle?: string } | null>(null);
  const [showReflectionPrompt, setShowReflectionPrompt] = useState<boolean>(false);
  const [reflectionPromptIssue, setReflectionPromptIssue] = useState<string | null>(null);

  const [songSubmissionFormData, setSongSubmissionFormData] = useState<SongSubmissionFormData>({
    songTitle: '',
    artist: '',
    whyItMatters: '',
    tagList: [],
    trackLink: '',
    submittedName: '',
    submittedEmail: '',
  });

  const [songEngagements, setSongEngagements] = useState<UserSongEngagement[]>([]);

  // ===== SUPABASE MIGRATION: Remove all localStorage persistence =====
  // Data now persists in Supabase via user_events and profile tables
  // ===== END SUPABASE MIGRATION =====

  // Load songs and playlists from Supabase
  const loadDataFromSupabase = async () => {
    setIsLoadingData(true);
    try {
      // Fetch published songs from Supabase
      const songs = await getSongs({ status: 'published' });
      console.log('[AppContext] Loaded songs from database:', songs.length, 'songs');
      console.log('[AppContext] Song IDs:', songs.map(s => s.id));
      const transformedSongs = transformSongDataToCards(songs);
      setAllSongs(transformedSongs);

      // Fetch playlists from Supabase
      const playlists = await getAllPlaylists();
      const transformedPlaylists = transformPlaylistsToUI(playlists);
      setAllPlaylists(transformedPlaylists);

      console.log(`[AppContext] Loaded ${songs.length} songs and ${playlists.length} playlists from Supabase`);
    } catch (error) {
      console.error('[AppContext] Error loading data from Supabase:', error);
      // Fallback to empty arrays on error
      setAllSongs([]);
      setAllPlaylists([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    loadDataFromSupabase();
  }, []);

  // ===== SUPABASE MIGRATION: Load ALL user data from Supabase when user logs in =====
  useEffect(() => {
    if (!user) {
      // Guest mode - reset to defaults
      setUserProfile(null);
      setSavedCardIds([]);
      setIdentityMoments([]);
      setMoodEntries([]);
      setCardViewHistory({});
      setViewedCardIds([]);
      return;
    }

    const loadUserData = async () => {
      try {
        // Load profile and derived data in parallel
        const [
          dbProfile,
          dbSavedSongs,
          derivedMoments,
          derivedMoods,
          derivedHistory,
        ] = await Promise.all([
          getProfile(user.id),
          getUserSavedSongs(user.id),
          getDerivedIdentityMoments(user.id),
          getDerivedMoodEntries(user.id),
          getDerivedCardViewHistory(user.id),
        ]);

        // Set profile data
        if (dbProfile) {
          console.log('[AppContext] Loading profile with interests:', dbProfile.interests);
          setUserProfile({
            id: user.id,
            username: dbProfile.display_name || 'User',
            interests: dbProfile.interests || [],
            cardsExplored: Object.keys(derivedHistory).length,
            daysActive: 1, // Could compute from user_events created_at dates
          });
        }

        // Set saved songs
        const savedIds = dbSavedSongs.map(s => s.song_id);
        console.log('[AppContext] Loading saved songs:', savedIds.length, 'songs:', savedIds);
        setSavedCardIds(savedIds);

        // Set derived identity moments
        setIdentityMoments(derivedMoments);

        // Set derived mood entries
        setMoodEntries(derivedMoods);

        // Set card view history
        setCardViewHistory(derivedHistory);

        // Set viewed card IDs (unique keys from history)
        setViewedCardIds(Object.keys(derivedHistory));

      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [user]);
  // ===== END SUPABASE MIGRATION =====

  // Handle hash-based routing for password reset and auth callbacks
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      console.log('[AppContext] Hash changed:', hash);
      console.log('[AppContext] Full URL:', window.location.href);

      // Parse hash route and query params
      // Format can be: #/reset-password?access_token=xxx or #access_token=xxx
      const hashParts = hash.substring(1).split('?');
      const hashRoute = hashParts[0]; // e.g., "/reset-password" or ""
      const queryString = hashParts[1] || hashParts[0]; // Query params might be in first or second part
      const params = new URLSearchParams(queryString.startsWith('/') ? '' : queryString);

      console.log('[AppContext] Parsed - hashRoute:', hashRoute, 'queryString:', queryString);
      console.log('[AppContext] Has access_token:', hash.includes('access_token'));
      console.log('[AppContext] Has error:', hash.includes('error'));

      // Check if this is a Supabase auth callback with access_token
      if (hash && hash.includes('access_token')) {
        const type = params.get('type');
        console.log('[AppContext] Auth callback detected - type:', type, 'hashRoute:', hashRoute);

        // Password recovery - route to reset-password screen
        if (type === 'recovery') {
          console.log('[AppContext] ✅ RECOVERY TYPE - Routing to reset-password screen');
          setCurrentScreen('reset-password');
          return;
        }

        // Other auth types (signup, magiclink) - handled by EmailVerificationTracker
        // Don't change screen, let normal flow continue
        console.log('[AppContext] Non-recovery auth type, letting normal flow handle it');
        return;
      }

      // Check if this is a password reset error callback
      // Supabase redirects with error params when reset link is invalid/expired
      // Format: #error=access_denied&error_code=otp_expired&error_description=Email+link+is+invalid...
      if (hash && hash.includes('error') && hash.includes('error_description')) {
        const errorCode = params.get('error_code');
        const errorDesc = params.get('error_description') || '';
        console.log('[AppContext] Error callback detected - errorCode:', errorCode, 'errorDesc:', errorDesc);

        // If it's an OTP/email link error, route to reset-password to show error
        if (errorCode === 'otp_expired' || errorCode === 'otp_disabled' ||
            errorCode === 'access_denied' ||
            errorDesc.includes('Email+link') || errorDesc.includes('invalid') || errorDesc.includes('expired')) {
          console.log('[AppContext] ⚠️ PASSWORD RESET ERROR - Routing to reset-password screen with error in URL');

          // Rewrite the hash to include the /reset-password route so the screen can parse it
          const newHash = `#/reset-password?${queryString}`;
          console.log('[AppContext] Rewriting hash to:', newHash);
          window.location.hash = newHash;
          setCurrentScreen('reset-password');
          return;
        }
      }

      // Manual hash navigation
      if (hashRoute.startsWith('/reset-password')) {
        console.log('[AppContext] 🔗 Manual hash navigation to reset-password');
        setCurrentScreen('reset-password');
      } else if (hashRoute.startsWith('/auth')) {
        console.log('[AppContext] 🔗 Manual hash navigation to auth');
        setCurrentScreen('auth');
      } else {
        console.log('[AppContext] No special routing needed for hash:', hashRoute);
      }
    };

    // Check immediately on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const updateUserInterests = async (interests: string[]) => {
    if (userProfile) {
      setUserProfile({
        ...userProfile,
        interests,
      });
    }

    // Sync to Supabase user_preferences for personalization
    if (user) {
      await upsertUserPreferences(user.id, interests);
    }
  };

  // DEPRECATED: Removed destructive reset feature
  // Users can now safely retune their feed via Settings > Retune my feed
  // which only updates interests without deleting saved data
  /*
  const resetProfile = async () => {
    if (!user) {
      // Guest mode - just reset UI state
      setSavedCardIds([]);
      setSavedPlaylistIds([]);
      setViewedCardIds([]);
      setIdentityMoments([]);
      setCardViewHistory({});
      setMoodEntries([]);
      setSelectedTopicFilter('All');

      if (userProfile) {
        setUserProfile({
          ...userProfile,
          cardsExplored: 0,
          daysActive: 1,
        });
      }

      navigateTo('home');
      return;
    }

    // Authenticated user - delete all data from database
    console.log('[resetProfile] Starting profile reset for user:', user.id);
    const result = await deleteAllUserData(user.id);
    console.log('[resetProfile] Reset result:', result);

    if (result.success) {
      // Clear localStorage to prevent OnboardingSync from restoring old data
      console.log('[resetProfile] Clearing localStorage...');
      localStorage.removeItem('onboarding_topics');
      localStorage.removeItem('onboarding_completed');

      // Wait a moment to ensure database writes complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reload the page to clear all React state and caches
      console.log('[resetProfile] Reloading page...');
      window.location.href = '/';
    } else {
      console.error('[resetProfile] Failed to reset profile:', result.error);
      throw new Error(result.error || 'Failed to reset profile');
    }
  };
  */

  const navigateTo = (screen: string, cardId?: string, state?: any) => {
    // Add current screen and cardId to history before navigating
    if (currentScreen) {
      setNavigationHistory(prev => [...prev, { screen: currentScreen, cardId: currentCardId, state: null }]);
    }
    console.log('[AppContext] navigateTo:', { screen, cardId, currentCardId: cardId || null });
    setCurrentScreen(screen);
    setCurrentCardId(cardId || null);
    setRestorationState(state || null);
    setShowActionWheel(false);
    window.scrollTo(0, 0);
  };

  const navigateReplacingCurrent = (screen: string, cardId?: string, state?: any) => {
    // Navigate to new screen without adding current screen to history
    // This effectively replaces the current screen in the navigation stack
    setCurrentScreen(screen);
    setCurrentCardId(cardId || null);
    setRestorationState(state || null);
    setShowActionWheel(false);
    window.scrollTo(0, 0);
  };

  const navigateBack = () => {
    if (navigationHistory.length > 0) {
      const previous = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      setCurrentScreen(previous.screen);
      setCurrentCardId(previous.cardId);
      setRestorationState(previous.state || null);
      setShowActionWheel(false);
      window.scrollTo(0, 0);
    } else {
      // Fallback when no history: return to home
      setCurrentScreen('home');
      setCurrentCardId(null);
      setRestorationState(null);
      setShowActionWheel(false);
      window.scrollTo(0, 0);
    }
  };

  const clearRestorationState = () => {
    setRestorationState(null);
  };

  const toggleSaveCard = async (cardId: string) => {
    const wasSaved = savedCardIds.includes(cardId);

    // ===== SUPABASE MIGRATION: Save/unsave to database =====
    if (user) {
      if (wasSaved) {
        await unsaveSongForUser(user.id, cardId);
      } else {
        await saveSongForUser(user.id, cardId);
      }
    }
    // ===== END SUPABASE MIGRATION =====

    // Track collection add/remove for analytics
    trackEvent({
      event_name: wasSaved ? 'collection_removed' : 'collection_added',
      song_id: cardId,
    });

    setSavedCardIds(prev =>
      wasSaved
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );

    setSongEngagements(prev => {
      const existing = prev.find(e => e.songId === cardId);
      const now = new Date().toISOString();

      if (existing) {
        return prev.map(e =>
          e.songId === cardId
            ? { ...e, saved: !wasSaved, lastEngagedAt: now }
            : e
        );
      } else {
        return [...prev, { songId: cardId, saved: !wasSaved, lastEngagedAt: now }];
      }
    });
  };

  const toggleSavePlaylist = (playlistId: string) => {
    setSavedPlaylistIds(prev =>
      prev.includes(playlistId)
        ? prev.filter(id => id !== playlistId)
        : [...prev, playlistId]
    );
  };

  const markCardViewed = (cardId: string) => {
    const now = new Date().toISOString();

    setSongEngagements(prev => {
      const existing = prev.find(e => e.songId === cardId);

      if (existing) {
        return prev.map(e =>
          e.songId === cardId
            ? { ...e, lastEngagedAt: now }
            : e
        );
      } else {
        return [...prev, { songId: cardId, saved: false, lastEngagedAt: now }];
      }
    });

    if (!viewedCardIds.includes(cardId)) {
      setViewedCardIds(prev => [...prev, cardId]);

      setCardViewHistory(prev => ({
        ...prev,
        [cardId]: (prev[cardId] || 0) + 1,
      }));

      if (userProfile) {
        setUserProfile({
          ...userProfile,
          cardsExplored: userProfile.cardsExplored + 1,
        });
      }

      const card = getAllCards().find(c => c.id === cardId);
      if (card && viewedCardIds.length > 0 && (viewedCardIds.length + 1) % 5 === 0) {
        const recentCards = getAllCards().filter(c =>
          viewedCardIds.slice(-5).includes(c.id) || c.id === cardId
        );

        const categoryCount: Record<string, number> = {};
        recentCards.forEach(c => {
          categoryCount[c.broadCategory] = (categoryCount[c.broadCategory] || 0) + 1;
        });

        const topCategory = Object.entries(categoryCount)
          .sort(([, a], [, b]) => b - a)[0];

        if (topCategory && topCategory[1] >= 3) {
          setReflectionPromptIssue(topCategory[0]);
          setTimeout(() => setShowReflectionPrompt(true), 500);
        }
      }

      const todaysHero = getTodaysHeroSong(getAllCards());
      if (todaysHero && todaysHero.id === cardId) {
        recordHeroViewed(cardId);
      }
    }
  };

  const setTopicFilter = (topic: string) => {
    setSelectedTopicFilter(topic);

    // Track topic filter selection for analytics (skip 'All' filter)
    if (topic !== 'All') {
      trackEvent({
        event_name: 'topic_filter_selected',
        topic_tag: topic,
      });
    }
  };

  const getCards = (): StoryCard[] => {
    // Use Supabase data loaded from database
    const interests = userProfile?.interests || [];

    if (selectedTopicFilter === 'All') {
      return sortHomeCards(allSongs, interests);
    }
    return sortFilteredCards(allSongs, selectedTopicFilter as IssueTag);
  };

  const getPlaylists = (): Playlist[] => {
    // Use Supabase data loaded from database
    const interests = userProfile?.interests || [];
    return sortPlaylists(allPlaylists, interests);
  };

  const getFeaturedCard = (): StoryCard | null => {
    // Use Supabase data loaded from database
    return getTodaysHeroSong(allSongs);
  };

  // Use Supabase data loaded from database
  const getAllCards = (): StoryCard[] => {
    return allSongs;
  };

  const addIdentityMoment = (cardId: string, type: IdentityMomentType, issueTags: string[]) => {
    const moment: IdentityMoment = {
      cardId,
      type,
      issueTags,
      timestamp: Date.now(),
    };
    setIdentityMoments(prev => [...prev, moment]);
  };

  const removeIdentityMoment = (cardId: string, type: IdentityMomentType) => {
    setIdentityMoments(prev =>
      prev.filter(m => !(m.cardId === cardId && m.type === type))
    );
  };

  const toggleIdentityMoment = (cardId: string, type: IdentityMomentType, issueTags: string[]) => {
    const exists = identityMoments.some(m => m.cardId === cardId && m.type === type);
    if (exists) {
      removeIdentityMoment(cardId, type);
    } else {
      addIdentityMoment(cardId, type, issueTags);
    }
  };

  const getIdentityInsights = (): IdentityInsights => {
    const resonatesCount = identityMoments.filter(m => m.type === 'resonates').length;
    const worriedCount = identityMoments.filter(m => m.type === 'worried').length;
    const curiousCount = identityMoments.filter(m => m.type === 'curious').length;

    const categoryMap = new Map<string, number>();
    identityMoments.forEach(moment => {
      const card = getAllCards().find(c => c.id === moment.cardId);
      if (card) {
        // Count primary tag
        categoryMap.set(card.primaryIssueTag, (categoryMap.get(card.primaryIssueTag) || 0) + 1);
        // Count all secondary tags
        card.secondaryIssueTags.forEach(tag => {
          categoryMap.set(tag, (categoryMap.get(tag) || 0) + 1);
        });
      }
    });

    const topIssues = Array.from(categoryMap.entries())
      .map(([issue, count]) => ({ issue, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    let dominantEmotion: IdentityMomentType | null = null;
    if (resonatesCount > worriedCount && resonatesCount > curiousCount) {
      dominantEmotion = 'resonates';
    } else if (worriedCount > resonatesCount && worriedCount > curiousCount) {
      dominantEmotion = 'worried';
    } else if (curiousCount > resonatesCount && curiousCount > worriedCount) {
      dominantEmotion = 'curious';
    }

    return {
      resonatesCount,
      worriedCount,
      curiousCount,
      topIssues,
      dominantEmotion,
    };
  };

  const getMeaningLayers = (): MeaningLayers => {
    const insights = getIdentityInsights();

    const mostViewedStory = Object.entries(cardViewHistory).length > 0
      ? Object.entries(cardViewHistory)
          .sort(([, a], [, b]) => b - a)
          .map(([cardId, viewCount]) => {
            const card = getAllCards().find(c => c.id === cardId);
            return card
              ? {
                  songTitle: card.songTitle,
                  artistName: card.artistName,
                  viewCount,
                }
              : null;
          })
          .filter(Boolean)[0] || null
      : null;

    const allViewedCards = getAllCards().filter(c => viewedCardIds.includes(c.id));
    const categoryViewCount: Record<string, number> = {};
    allViewedCards.forEach(card => {
      categoryViewCount[card.broadCategory] = (categoryViewCount[card.broadCategory] || 0) + 1;
    });
    const topViewedIssue = Object.entries(categoryViewCount)
      .sort(([, a], [, b]) => b - a)[0];

    const currentStreak = userProfile?.daysActive || 0;
    const totalEngagements = identityMoments.length + viewedCardIds.length;

    return {
      topIssue: topViewedIssue ? topViewedIssue[0] : null,
      mostResonantTheme: insights.topIssues[0]?.issue || null,
      mostViewedStory,
      monthlyCivicPulse: {
        currentStreak,
        emotionalPattern: insights.dominantEmotion,
        totalEngagements,
      },
    };
  };

  const showToast = (title: string, subtitle?: string) => {
    setToastMessage({ title, subtitle });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const clearToast = () => {
    setToastMessage(null);
  };

  const dismissReflectionPrompt = () => {
    setShowReflectionPrompt(false);
    setReflectionPromptIssue(null);
  };

  const trackMood = (cardId: string, mood: CivicMood, songTitle: string, artistName: string) => {
    const existingIndex = moodEntries.findIndex(entry => entry.cardId === cardId);

    if (existingIndex >= 0) {
      const updated = [...moodEntries];
      const currentMoods = updated[existingIndex].moods;

      if (currentMoods.includes(mood)) {
        updated[existingIndex] = {
          ...updated[existingIndex],
          moods: currentMoods.filter(m => m !== mood),
          timestamp: Date.now(),
        };
      } else {
        updated[existingIndex] = {
          ...updated[existingIndex],
          moods: [...currentMoods, mood],
          timestamp: Date.now(),
        };
      }
      setMoodEntries(updated);
    } else {
      setMoodEntries([
        ...moodEntries,
        {
          cardId,
          moods: [mood],
          timestamp: Date.now(),
          songTitle,
          artistName,
        },
      ]);
    }
  };

  const getMoodsForCard = (cardId: string): CivicMood[] => {
    const entry = moodEntries.find(e => e.cardId === cardId);
    return entry && Array.isArray(entry.moods) ? entry.moods : [];
  };

  const getMoodFrequency = (): MoodFrequency => {
    const now = Date.now();
    const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);

    const recentEntries = moodEntries.filter(entry => entry.timestamp >= thirtyDaysAgo);

    const frequency: MoodFrequency = {
      Moved: 0,
      Empowered: 0,
      Curious: 0,
      Heavy: 0,
    };

    recentEntries.forEach(entry => {
      entry.moods.forEach(mood => {
        frequency[mood]++;
      });
    });

    return frequency;
  };

  const getRecentMoodEntries = (limit: number = 10): MoodEntry[] => {
    return [...moodEntries]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);
  };

  const findRelatedStory = (cardId: string): StoryCard | null => {
    const currentCard = getAllCards().find(c => c.id === cardId);
    if (!currentCard) return null;

    const candidates = getAllCards().filter(c => {
      if (c.id === cardId) return false;
      if (c.broadCategory !== currentCard.broadCategory) return false;
      return true;
    });

    const withSharedTags = candidates.filter(c =>
      c.subTags.some(tag => currentCard.subTags.includes(tag))
    );

    if (withSharedTags.length > 0) {
      return withSharedTags[Math.floor(Math.random() * withSharedTags.length)];
    }

    if (candidates.length > 0) {
      return candidates[Math.floor(Math.random() * candidates.length)];
    }

    return null;
  };

  const getMeaningMix = (): StoryCard[] => {
    const allViewedCards = getAllCards().filter(c => viewedCardIds.includes(c.id));
    const categoryEngagement: Record<string, number> = {};

    allViewedCards.forEach(card => {
      categoryEngagement[card.broadCategory] = (categoryEngagement[card.broadCategory] || 0) + 1;
    });

    identityMoments.forEach(moment => {
      const card = getAllCards().find(c => c.id === moment.cardId);
      if (card) {
        categoryEngagement[card.broadCategory] = (categoryEngagement[card.broadCategory] || 0) + 2;
      }
    });

    savedCardIds.forEach(cardId => {
      const card = getAllCards().find(c => c.id === cardId);
      if (card) {
        categoryEngagement[card.broadCategory] = (categoryEngagement[card.broadCategory] || 0) + 3;
      }
    });

    const topCategories = Object.entries(categoryEngagement)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);

    if (topCategories.length === 0) {
      return getAllCards().slice(0, 5);
    }

    const recommendations: StoryCard[] = [];
    const usedIds = new Set<string>();

    topCategories.forEach(category => {
      const categoryCards = getAllCards().filter(c =>
        c.broadCategory === category && !viewedCardIds.includes(c.id) && !usedIds.has(c.id)
      );

      const diverseCards = categoryCards.slice(0, 2);
      diverseCards.forEach(card => {
        recommendations.push(card);
        usedIds.add(card.id);
      });
    });

    const unseenCards = getAllCards().filter(c => !viewedCardIds.includes(c.id) && !usedIds.has(c.id));
    while (recommendations.length < 5 && unseenCards.length > 0) {
      const randomCard = unseenCards.splice(Math.floor(Math.random() * unseenCards.length), 1)[0];
      recommendations.push(randomCard);
      usedIds.add(randomCard.id);
    }

    return recommendations.slice(0, 5);
  };

  const updateSongSubmissionFormData = (data: Partial<SongSubmissionFormData>) => {
    setSongSubmissionFormData(prev => ({ ...prev, ...data }));
  };

  const clearSongSubmissionFormData = () => {
    setSongSubmissionFormData({
      songTitle: '',
      artist: '',
      whyItMatters: '',
      tagList: '',
      trackLink: '',
      submittedName: '',
      submittedEmail: '',
    });
  };

  const refreshSongs = async () => {
    await loadDataFromSupabase();
  };

  const refreshUserData = async () => {
    if (!user) {
      // Guest mode - reset to defaults
      setUserProfile(null);
      setSavedCardIds([]);
      setIdentityMoments([]);
      setMoodEntries([]);
      setCardViewHistory({});
      setViewedCardIds([]);
      return;
    }

    try {
      // Load profile and derived data in parallel
      const [
        dbProfile,
        dbSavedSongs,
        derivedMoments,
        derivedMoods,
        derivedHistory,
      ] = await Promise.all([
        getProfile(user.id),
        getUserSavedSongs(user.id),
        getDerivedIdentityMoments(user.id),
        getDerivedMoodEntries(user.id),
        getDerivedCardViewHistory(user.id),
      ]);

      // Set profile data
      if (dbProfile) {
        console.log('[AppContext] Refreshing profile with interests:', dbProfile.interests);
        setUserProfile({
          id: user.id,
          username: dbProfile.display_name || 'User',
          interests: dbProfile.interests || [],
          cardsExplored: Object.keys(derivedHistory).length,
          daysActive: 1,
        });
      }

      // Set saved songs
      const savedIds = dbSavedSongs.map(s => s.song_id);
      setSavedCardIds(savedIds);

      // Set derived identity moments
      setIdentityMoments(derivedMoments);

      // Set derived mood entries
      setMoodEntries(derivedMoods);

      // Set card view history
      setCardViewHistory(derivedHistory);

      // Set viewed card IDs
      setViewedCardIds(Object.keys(derivedHistory));

      console.log('[AppContext] User data refreshed successfully');
    } catch (error) {
      console.error('[AppContext] Error refreshing user data:', error);
    }
  };

  return (
    <AppContext.Provider
      value={{
        userProfile,
        savedCardIds,
        savedPlaylistIds,
        viewedCardIds,
        identityMoments,
        cardViewHistory,
        currentScreen,
        currentCardId,
        restorationState,
        selectedTopicFilter,
        showActionWheel,
        toastMessage,
        showReflectionPrompt,
        reflectionPromptIssue,
        songSubmissionFormData,
        isLoadingData,
        updateUserInterests,
        // resetProfile, // DEPRECATED: Removed destructive reset feature
        navigateTo,
        navigateReplacingCurrent,
        navigateBack,
        clearRestorationState,
        setCurrentCardId,
        toggleSaveCard,
        toggleSavePlaylist,
        markCardViewed,
        setTopicFilter,
        setShowActionWheel,
        addIdentityMoment,
        removeIdentityMoment,
        toggleIdentityMoment,
        getIdentityInsights,
        getMeaningLayers,
        showToast,
        clearToast,
        dismissReflectionPrompt,
        getCards,
        getPlaylists,
        getFeaturedCard,
        trackMood,
        getMoodsForCard,
        getMoodFrequency,
        getRecentMoodEntries,
        findRelatedStory,
        getMeaningMix,
        moodEntries,
        songEngagements,
        updateSongSubmissionFormData,
        clearSongSubmissionFormData,
        refreshSongs,
        refreshUserData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}

if (typeof window !== 'undefined') {
  (window as any).resetCivicBeat = () => {
    // ===== SUPABASE MIGRATION: No localStorage to clear, just reload =====
    window.location.reload();
    console.log('CivicBeat refreshed. Data persists in Supabase.');
    // ===== END SUPABASE MIGRATION =====
  };
}
